import React, { useState } from 'react';
import { Select, Button, Space, Modal, Input, message, Popconfirm } from 'antd';
import { PlusOutlined, CopyOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useScenarios } from '../../contexts/ScenarioContext';
import { formatDistanceToNow } from 'date-fns';
import { da } from 'date-fns/locale';

/**
 * Scenario Selector Component
 * Dropdown for switching between simulation scenarios
 */
const ScenarioSelector = ({ onScenarioChange }) => {
  const {
    scenarios,
    activeScenarioId,
    setActiveScenario,
    createScenario,
    duplicateScenario,
    deleteScenario,
    exportScenario,
    importScenario,
  } = useScenarios();

  const [newScenarioModalOpen, setNewScenarioModalOpen] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDescription, setNewScenarioDescription] = useState('');

  const handleScenarioChange = (scenarioId) => {
    setActiveScenario(scenarioId);
    if (onScenarioChange) {
      onScenarioChange(scenarioId);
    }
  };

  const handleCreateScenario = async () => {
    if (!newScenarioName.trim()) {
      message.error('Indtast venligst et navn til scenariet');
      return;
    }

    try {
      // Get current config from active scenario or use defaults
      const activeScenario = scenarios.find(s => s.id === activeScenarioId);
      const defaultConfig = activeScenario?.config || {
        heater_MW: 30,
        turbine_MW: 10,
        htes_modules: 2,
        ltes_capacity: 205,
        battery_MWh: 3,
        cop: 0.9,
        turbine_efficiency: { heat: 0.85, electricity: 0.15 },
        storage_efficiency: 0.95,
        an_plant_loss: 0.03,
        ab_plant_loss: 0.10,
      };

      await createScenario(newScenarioName, defaultConfig, newScenarioDescription);
      message.success(`Scenarie "${newScenarioName}" oprettet`);
      setNewScenarioModalOpen(false);
      setNewScenarioName('');
      setNewScenarioDescription('');
    } catch (error) {
      message.error('Kunne ikke oprette scenarie');
      console.error(error);
    }
  };

  const handleDuplicate = async () => {
    if (!activeScenarioId) return;
    
    try {
      const activeScenario = scenarios.find(s => s.id === activeScenarioId);
      await duplicateScenario(activeScenarioId, `${activeScenario.name} (kopi)`);
      message.success('Scenarie kopieret');
    } catch (error) {
      message.error('Kunne ikke kopiere scenarie');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!activeScenarioId || scenarios.length <= 1) {
      message.warning('Du skal have mindst ét scenarie');
      return;
    }
    
    try {
      await deleteScenario(activeScenarioId);
      message.success('Scenarie slettet');
    } catch (error) {
      message.error('Kunne ikke slette scenarie');
      console.error(error);
    }
  };

  const handleExport = () => {
    if (!activeScenarioId) return;
    
    try {
      const jsonString = exportScenario(activeScenarioId);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const scenario = scenarios.find(s => s.id === activeScenarioId);
      a.download = `${scenario.name.replace(/\s+/g, '-')}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Scenarie eksporteret');
    } catch (error) {
      message.error('Kunne ikke eksportere scenarie');
      console.error(error);
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        await importScenario(text);
        message.success('Scenarie importeret');
      } catch (error) {
        message.error('Kunne ikke importere scenarie');
        console.error(error);
      }
    };
    input.click();
  };

  const formatScenarioOption = (scenario) => {
    const timeAgo = formatDistanceToNow(new Date(scenario.timestamp), {
      addSuffix: true,
      locale: da,
    });
    
    return (
      <div>
        <div style={{ fontWeight: 500 }}>{scenario.name}</div>
        <div style={{ fontSize: '0.75rem', color: '#666' }}>
          {scenario.description || timeAgo}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <Select
        value={activeScenarioId}
        onChange={handleScenarioChange}
        style={{ minWidth: 200 }}
        placeholder="Vælg scenarie"
        optionLabelProp="label"
      >
        {scenarios.map(scenario => (
          <Select.Option 
            key={scenario.id} 
            value={scenario.id}
            label={scenario.name}
          >
            {formatScenarioOption(scenario)}
          </Select.Option>
        ))}
      </Select>

      <Space.Compact>
        <Button
          icon={<PlusOutlined />}
          onClick={() => setNewScenarioModalOpen(true)}
          title="Nyt scenarie"
        >
          Ny
        </Button>
        
        <Button
          icon={<CopyOutlined />}
          onClick={handleDuplicate}
          disabled={!activeScenarioId}
          title="Kopier scenarie"
        />
        
        <Popconfirm
          title="Slet scenarie"
          description="Er du sikker på at du vil slette dette scenarie?"
          onConfirm={handleDelete}
          okText="Ja, slet"
          cancelText="Annuller"
          disabled={!activeScenarioId || scenarios.length <= 1}
        >
          <Button
            icon={<DeleteOutlined />}
            disabled={!activeScenarioId || scenarios.length <= 1}
            danger
            title="Slet scenarie"
          />
        </Popconfirm>
        
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          disabled={!activeScenarioId}
          title="Eksporter scenarie"
        />
        
        <Button
          icon={<UploadOutlined />}
          onClick={handleImport}
          title="Importer scenarie"
        />
      </Space.Compact>

      <Modal
        title="Opret nyt scenarie"
        open={newScenarioModalOpen}
        onOk={handleCreateScenario}
        onCancel={() => {
          setNewScenarioModalOpen(false);
          setNewScenarioName('');
          setNewScenarioDescription('');
        }}
        okText="Opret"
        cancelText="Annuller"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Navn *
            </label>
            <Input
              value={newScenarioName}
              onChange={(e) => setNewScenarioName(e.target.value)}
              placeholder="F.eks. Standard konfiguration"
              onPressEnter={handleCreateScenario}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Beskrivelse (valgfri)
            </label>
            <Input.TextArea
              value={newScenarioDescription}
              onChange={(e) => setNewScenarioDescription(e.target.value)}
              placeholder="Beskrivelse af scenariet..."
              rows={3}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default ScenarioSelector;
